/**
 * web/src/lib/billing.ts — Pear Plus client (card 32BEnNO6I2kx).
 *
 * One listener does the whole upgrade-prompt job: `api()` dispatches
 * `pear:plus-required` when the worker answers 402 with that code, and the
 * UpgradeSheet opens. Every gated tap in the app therefore gets the paywall
 * prompt without each screen knowing billing exists.
 */
import { api, API_BASE } from './api';

export interface Entitlement {
  plan: 'essentials' | 'plus';
  plus: boolean;
  customerId: string | null;
  subscriptionId: string | null;
  priceId: string | null;
  interval: 'month' | 'year' | null;
  status: string | null;
  currentPeriodEnd: number | null;
  plusSince: number | null;
  /** Trial end (ms). Set once, at couple creation (card ImIQwJDLW6Xs). */
  trialEnd: number | null;
  /** True while the trial is what is granting access — Plus without a sub. */
  trialing: boolean;
  /** Promo grant on the couple (card yWsCXHAXQ3Ej). A grant is Plus that was
   *  GIVEN, not bought: no Stripe customer, so there is no portal to manage.
   *  `promoEnd` null = indefinite (the plus2199 case). */
  promoCode: string | null;
  promoEnd: number | null;
  conciergeUsed: number;
  conciergeAllowance: number | null;
}

export interface BillingPrices {
  month: { lookupKey: string; cents: number; currency: string; label: string };
  year: { lookupKey: string; cents: number; currency: string; label: string };
}

export interface BillingState {
  entitlement: Entitlement;
  prices: BillingPrices;
  surfaces: string[];
  enforce: boolean;
  configured: boolean;
}

export const PLUS_EVENT = 'pear:plus-required';

/** Fire the upgrade prompt from anywhere (a screen that parsed its own
 *  error, an offline catch, the Settings upsell row). */
export function announcePlusRequired(): void {
  window.dispatchEvent(new CustomEvent(PLUS_EVENT));
}

export function fetchBillingState(): Promise<BillingState> {
  return api<BillingState>('/api/billing/state');
}

/** Hosted Stripe Checkout. The response is a URL; the browser leaves for it. */
export async function startCheckout(interval: 'month' | 'year'): Promise<string> {
  const res = await api<{ url: string }>('/api/billing/checkout', { body: { interval } });
  return res.url;
}

export async function startPortal(): Promise<string> {
  const res = await api<{ url: string }>('/api/billing/portal', { body: {} });
  return res.url;
}

/** What the paywall sheet renders while the state call is in flight. */
export const FALLBACK_PRICES: BillingPrices = {
  month: { lookupKey: 'pear_plus_monthly', cents: 999, currency: 'usd', label: '$9.99/month' },
  year: { lookupKey: 'pear_plus_yearly', cents: 7999, currency: 'usd', label: '$79.99/year' },
};

/** The Plus surfaces, in the order the sheet lists them. Kept in sync
 *  with shared/billing.ts by probe-billing. */
export const PLUS_FEATURES: { key: string; label: string }[] = [
  { key: 'gift_concierge', label: 'Gifting concierge' },
  { key: 'date_concierge', label: 'Date concierge' },
  /* "Unlimited concierge" is GONE from the sheet (founder email 2026-10-21,
     "Plus pricing overview remove unlimited concierge"). It was the one line
     that sold a quota rather than a capability, and it read as a licence to
     burn model spend. The underlying gate key is untouched — this list is
     display only, and shared/billing.ts still owns the real allowance. */
  { key: 'weekly_briefing', label: 'Weekly briefing' },
  { key: 'email_inbox', label: 'Email inbox' },
  { key: 'interest_digest', label: 'Partner interest digest' },
  { key: 'games', label: 'Games with the concierge' },
  { key: 'notebook_filing', label: 'Notebook smart filing' },
  { key: 'voice_transcription', label: 'Voice & meeting transcription' },
];

export { API_BASE };
